<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import Logo from '@/assets/Apexx_LOGO.webp';
import {
	getIdentifierFieldConfig,
	handlePhoneIdentifierFocus,
	toggleIdentifierMode,
	type IdentifierMode,
} from '@/auth/otpIdentifierMode';
import { requestPhoneNumberHint } from '@/auth/requestPhoneNumberHint';
import { rememberLastIdentityFromToken } from '@/auth/lastIdentity';
import { completeOtpApiLogin } from '@/auth/setup';
import { resumePendingPushIfAny } from '@/push/setupPushNavigation';
import { resumePendingShareIfAny } from '@/share/setupShareTarget';
import { useHubAuthStore } from '@/store/hubAuthStore';
import { OtpApiRequestError, sendOtpApi } from '@apexx/auth';
import { Button, Icon, Input, Typography, Wizard } from '@apexx/component-library';
import { motion } from 'motion-v';

const easeOut = [0.4, 0, 0.2, 1] as const;

const pageEnterTransition = {
	duration: 0.4,
	ease: easeOut,
};

const logoTransition = {
	type: 'spring' as const,
	stiffness: 380,
	damping: 32,
};

type UserType = 'client' | 'staff';
type Step = 'identifier' | 'verify';

definePage({ name: 'login-otp', meta: { public: true } });

const authStore = useHubAuthStore();
const router = useRouter();
const route = useRoute();

async function resumePendingNavigationAfterAuth(): Promise<boolean> {
	if (await resumePendingPushIfAny(router, authStore.accessToken)) {
		return true;
	}

	return resumePendingShareIfAny(router, authStore.accessToken);
}

const step = ref<Step>('identifier');
const stepDirection = ref(1);
const identifierMode = ref<IdentifierMode>('phone');
const phoneHintAttempted = ref(false);
const identifier = ref('');
const otp = ref('');
const challengeId = ref<string>();
const destination = ref('');
const channel = ref('');
const errorMessage = ref<string>();
const sending = ref(false);
const verifying = ref(false);
const resendAfter = ref(0);
let resendTimer: ReturnType<typeof setInterval> | undefined;

const userType = computed<UserType>(() =>
	route.query.type === 'staff' ? 'staff' : 'client',
);

const loginTypeLabel = computed(() =>
	userType.value === 'client' ? 'Customer Login' : 'Employee Login',
);

const identifierField = computed(() =>
	getIdentifierFieldConfig(identifierMode.value),
);

const canResend = computed(() => resendAfter.value <= 0 && !sending.value);

const verifySubtitle = computed(() => {
	if (!destination.value) {
		return 'Enter the one-time code we sent you.';
	}
	if (channel.value === 'email') {
		return `A code was sent to ${destination.value}.`;
	}
	return `A WhatsApp code was sent to ${destination.value}.`;
});

const inputSurfaceClass = 'border! border-border! bg-surface-input/20!';

const identifierInputClass = computed(() =>
	errorMessage.value && step.value === 'identifier'
		? `${inputSurfaceClass} !border-critical`
		: inputSurfaceClass,
);

const otpInputClass = computed(() =>
	errorMessage.value && step.value === 'verify'
		? `${inputSurfaceClass} !border-critical`
		: inputSurfaceClass,
);

function switchIdentifierMode(): void {
	identifierMode.value = toggleIdentifierMode(identifierMode.value);
	identifier.value = '';
	errorMessage.value = undefined;
	phoneHintAttempted.value = false;
}

async function onIdentifierFocus(): Promise<void> {
	const result = await handlePhoneIdentifierFocus({
		mode: identifierMode.value,
		identifier: identifier.value,
		hintAttempted: phoneHintAttempted.value,
		requestHint: requestPhoneNumberHint,
	});
	phoneHintAttempted.value = result.hintAttempted;
	if (result.prefill && !identifier.value.trim()) {
		identifier.value = result.prefill;
	}
}

function clearResendTimer(): void {
	if (resendTimer) {
		clearInterval(resendTimer);
		resendTimer = undefined;
	}
}

function startResendCooldown(seconds: number): void {
	clearResendTimer();
	resendAfter.value = Math.max(0, seconds);
	if (resendAfter.value <= 0) {
		return;
	}
	resendTimer = setInterval(() => {
		resendAfter.value = Math.max(0, resendAfter.value - 1);
		if (resendAfter.value <= 0) {
			clearResendTimer();
		}
	}, 1000);
}

const cameFromReauth = computed(() => route.query.from === 'reauth');

async function handleBack(): Promise<void> {
	if (step.value === 'verify') {
		goToIdentifierStep();
		return;
	}
	await router.push({ name: cameFromReauth.value ? 'reauth' : 'login' });
}

function goToIdentifierStep(): void {
	stepDirection.value = -1;
	step.value = 'identifier';
	otp.value = '';
	challengeId.value = undefined;
	destination.value = '';
	channel.value = '';
	errorMessage.value = undefined;
	clearResendTimer();
	resendAfter.value = 0;
}

function mapError(err: unknown): string {
	if (err instanceof OtpApiRequestError) {
		return err.payload.error_description ?? err.message;
	}
	if (err instanceof Error) {
		return err.message;
	}
	return 'Something went wrong. Please try again.';
}

async function handleSend(isResend = false): Promise<void> {
	errorMessage.value = undefined;
	sending.value = true;
	try {
		const response = await sendOtpApi(
			isResend && challengeId.value
				? {
						identifier: identifier.value,
						challengeId: challengeId.value,
					}
				: { identifier: identifier.value },
		);
		challengeId.value = response.challenge_id;
		destination.value = response.destination;
		channel.value = response.channel;
		stepDirection.value = 1;
		step.value = 'verify';
		startResendCooldown(response.resend_after);
	} catch (err) {
		errorMessage.value = mapError(err);
		if (err instanceof OtpApiRequestError && err.payload.retry_after) {
			startResendCooldown(err.payload.retry_after);
		}
	} finally {
		sending.value = false;
	}
}

async function handleVerify(): Promise<void> {
	if (!challengeId.value) {
		errorMessage.value = 'Request a code first.';
		return;
	}
	errorMessage.value = undefined;
	verifying.value = true;
	try {
		await completeOtpApiLogin(challengeId.value, otp.value);
		const token = authStore.accessToken;
		if (token) {
			await rememberLastIdentityFromToken(token, {
				otpIdentifier: identifier.value,
				identifierMode: identifierMode.value,
			});
		}
		if (await resumePendingNavigationAfterAuth()) {
			return;
		}
		await router.replace({ name: 'launcher' });
	} catch (err) {
		errorMessage.value = mapError(err);
	} finally {
		verifying.value = false;
	}
}

async function maybeAutoContinueFromReauth(): Promise<void> {
	if (route.query.autoContinue !== '1') {
		return;
	}

	const prefill =
		typeof route.query.identifier === 'string'
			? route.query.identifier.trim()
			: '';
	if (!prefill) {
		return;
	}

	if (route.query.mode === 'email' || route.query.mode === 'phone') {
		identifierMode.value = route.query.mode;
	} else {
		identifierMode.value = prefill.includes('@') ? 'email' : 'phone';
	}

	identifier.value = prefill;
	await handleSend(false);
}

watch(
	() => authStore.isLoggedIn,
	async (loggedIn) => {
		if (loggedIn) {
			if (await resumePendingNavigationAfterAuth()) {
				return;
			}
			await router.replace({ name: 'launcher' });
		}
	},
);

onMounted(async () => {
	if (authStore.isLoggedIn) {
		if (await resumePendingNavigationAfterAuth()) {
			return;
		}
		await router.replace({ name: 'launcher' });
		return;
	}

	await maybeAutoContinueFromReauth();
});

onUnmounted(() => {
	clearResendTimer();
});
</script>

<template>
	<Wizard
		content-padding="lg"
		centered
		root-class="min-h-dvh"
		content-class="relative pb-[calc(2.5rem+var(--safe-area-bottom))]"
	>
		<template #header>
			<div class="flex flex-col items-center justify-between gap-10 py-20">
				<div class="relative">
					<motion.div
						:initial="{ opacity: 0, x: 40 }"
						:animate="{ opacity: 1, x: 0 }"
						:transition="logoTransition"
					>
						<button
							type="button"
							class="absolute top-3 -left-12 z-10 flex h-12 w-12 items-center justify-center text-white/80 hover:text-white"
							aria-label="Go back"
							@click="handleBack"
						>
							<Icon name="arrowLeft" class="scale-150" />
						</button>
						<img
							:src="Logo"
							alt="Apexx logo"
							class="h-16 overflow-hidden rounded"
						/>
					</motion.div>
				</div>
				<div>
					<motion.div
						:initial="{ opacity: 0, y: 8, scale: 0.96 }"
						:animate="{ opacity: 1, y: 0, scale: 1 }"
						:transition="{ ...pageEnterTransition, delay: 0.1 }"
					>
						<div class="rounded-full text-white">
							<Icon
								:name="
									userType === 'client' ? 'person' : 'machines'
								"
							/>
							{{ loginTypeLabel }}
						</div>
					</motion.div>
				</div>
			</div>
		</template>

		<motion.div
			:initial="{ opacity: 0, y: 20 }"
			:animate="{ opacity: 1, y: 0 }"
			:transition="{ duration: 0.3 }"
			class="mx-auto mt-6 flex w-full max-w-md flex-col gap-5"
		>
			<template v-if="step === 'identifier'">
				<Typography
					variant="headline"
					class="text-center text-on-surface"
				>
					Sign In
				</Typography>
				<Typography
					variant="body"
					class="text-center text-on-surface-muted"
				>
					{{ identifierField.subtitle }}
				</Typography>
				<Input
					v-model="identifier"
					:label="identifierField.label"
					:placeholder="identifierField.placeholder"
					:type="identifierField.type"
					:inputmode="identifierField.inputmode"
					:autocomplete="identifierField.autocomplete"
					:name="identifierField.name"
					:disabled="sending"
					:custom-class="identifierInputClass"
					@focus="onIdentifierFocus"
				/>
				<button
					type="button"
					class="ml-auto self-end text-body text-primary underline-offset-2 hover:underline disabled:opacity-50"
					:disabled="sending"
					@click="switchIdentifierMode"
				>
					{{ identifierField.toggleLabel }}
				</button>
				<div
					v-if="errorMessage"
					class="rounded-lg bg-error/10 px-4 py-3 text-left"
					role="alert"
				>
					<Typography variant="body" class="text-error">
						{{ errorMessage }}
					</Typography>
				</div>
				<Button
					variant="primary"
					size="lg"
					:full-width="true"
					class="text-white"
					:loading="sending"
					:disabled="!identifier.trim() || sending"
					@click="handleSend(false)"
				>
					Send code
				</Button>
			</template>

			<template v-else>
				<Typography
					variant="headline"
					class="text-center text-on-surface"
				>
					Enter verification code
				</Typography>
				<Typography
					variant="body"
					class="break-all text-center text-on-surface-muted"
				>
					{{ verifySubtitle }}
				</Typography>
				<Input
					v-model="otp"
					label="OTP"
					placeholder="4-digit code"
					type="tel"
					inputmode="numeric"
					autocomplete="one-time-code"
					name="one-time-code"
					:disabled="verifying"
					:custom-class="otpInputClass"
				/>
				<div
					v-if="errorMessage"
					class="rounded-lg bg-error/10 px-4 py-3 text-left"
					role="alert"
				>
					<Typography variant="body" class="text-error">
						{{ errorMessage }}
					</Typography>
				</div>
				<Button
					variant="primary"
					size="lg"
					:full-width="true"
					class="text-white"
					:loading="verifying"
					:disabled="!otp.trim() || verifying"
					@click="handleVerify"
				>
					Verify and continue
				</Button>
				<Button
					variant="secondary"
					size="lg"
					:full-width="true"
					:disabled="!canResend || sending"
					:loading="sending"
					@click="handleSend(true)"
				>
					{{
						canResend
							? 'Resend code'
							: `Resend in ${resendAfter}s`
					}}
				</Button>
				<Button
					variant="ghost"
					size="lg"
					class="border-0!"
					:full-width="true"
					:disabled="sending || verifying"
					@click="goToIdentifierStep"
				>
					{{ identifierField.useAnotherLabel }}
				</Button>
			</template>
		</motion.div>
	</Wizard>
</template>
